import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import { useAuth } from "../../auth/AuthProvider";
import AppLogo from "../../components/AppLogo";
import { Button } from "../../components/buttons/button";
import airtable from "../../lib/airtables";
import { artistAmbassadorFieldIds } from "../../lib/airtables/tables/ArtistAmbassadors";
import { artistAmbassadorEventFieldIds } from "../../lib/airtables/tables/ArtistAmbassadorEvents";
import { uploadAttachment, MAX_UPLOAD_BYTES } from "../../lib/airtables/uploadAttachment";
import { getSession } from "../../helpers/storage";
import { AMBASSADOR_ROLE, DWF_USER_ROLE } from "../../helpers/constants";

// Events with this many registrations are full.
const MAX_EVENT_CAPACITY = 4;

// How many dates an ambassador may offer in a single month:
//   "one"       — one date per month, picked with radio buttons
//   "unlimited" — any number of dates per month, picked with checkboxes
const DATES_PER_MONTH: "one" | "unlimited" = "one";
const singleDatePerMonth = DATES_PER_MONTH === "one";

// When true, a calendar date can only be claimed by one artist at a time.
// Flip to false to allow multiple artists to offer workshops on the same day.
const ONE_ARTIST_PER_DATE = true;

// Workshop date options offered for each month of the school year.
const DATE_OPTIONS: Record<string, number[]> = {
  October: [14, 21, 28],
  November: [4, 18, 25],
  December: [2, 9],
  January: [13, 20, 27],
  February: [3, 10, 17, 24],
  March: [3, 31],
  April: [7, 14, 21, 28],
  May: [5, 12, 19, 26],
  June: [2, 9, 16],
};

// School-year display order; must match the "Months Available" choices in Airtable.
const MONTH_ORDER = [
  "October", "November", "December", "January",
  "February", "March", "April", "May", "June",
];

const MONTH_INDEX: Record<string, number> = {
  January: 0, February: 1, March: 2, April: 3, May: 4, June: 5,
  October: 9, November: 10, December: 11,
};

// Fall months belong to the school year's starting calendar year.
const FALL_MONTHS = ["October", "November", "December"];

// Ambassadors host two 1-hour workshops on each date they offer.
const SESSION_TIMES = [
  { hour: 9, minute: 30, label: "9:30 AM ET" },
  { hour: 14, minute: 0, label: "2:00 PM ET" },
];

const ET_ZONE = "America/Toronto";

// The school year starts in the fall: from July onward the upcoming
// school year starts this calendar year, before July it started last year.
const schoolYearStart = () => {
  const now = new Date();
  return now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
};

const yearForMonth = (month: string) =>
  FALL_MONTHS.includes(month) ? schoolYearStart() : schoolYearStart() + 1;

/**
 * Build an ISO timestamp for a wall-clock Eastern time, handling DST by
 * testing both possible UTC offsets against what Intl reports for the zone.
 */
const easternTimeToISO = (
  year: number,
  monthIndex: number,
  day: number,
  hour: number,
  minute: number
) => {
  for (const offsetHours of [4, 5]) {
    const candidate = new Date(Date.UTC(year, monthIndex, day, hour + offsetHours, minute));
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: ET_ZONE,
      hourCycle: "h23",
      hour: "2-digit",
      minute: "2-digit",
    }).formatToParts(candidate);
    const h = Number(parts.find((p) => p.type === "hour")?.value);
    const m = Number(parts.find((p) => p.type === "minute")?.value);
    if (h === hour && m === minute) return candidate.toISOString();
  }
  return new Date(Date.UTC(year, monthIndex, day, hour + 5, minute)).toISOString();
};

/** Date key ("2026-10-07") for an event's timestamp, in Eastern time. */
const easternDateKey = (iso: string) => {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: ET_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(parsed);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || "";
  return `${get("year")}-${get("month")}-${get("day")}`;
};

const dateKeyFor = (month: string, day: number) =>
  `${yearForMonth(month)}-${String(MONTH_INDEX[month] + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

const formatSessionTime = (iso: string) => {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: ET_ZONE,
    hour: "numeric",
    minute: "2-digit",
  }).format(parsed);
};

const fieldIds = artistAmbassadorFieldIds;
const eventFieldIds = artistAmbassadorEventFieldIds;

export default function AmbassadorPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAmbassador = getSession(DWF_USER_ROLE) === AMBASSADOR_ROLE;

  const [record, setRecord] = useState<any>(false);
  const [events, setEvents] = useState<any[]>([]);
  // Date keys ("2026-10-07") already claimed by another artist.
  const [takenDateKeys, setTakenDateKeys] = useState<Set<string>>(new Set());
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState(false);
  const [busyDateKey, setBusyDateKey] = useState("");
  const [availabilityMessage, setAvailabilityMessage] = useState("");
  const [bio, setBio] = useState("");
  const [workshopTitle, setWorkshopTitle] = useState("");
  const [workshopDescription, setWorkshopDescription] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");
  const [tiktokUrl, setTiktokUrl] = useState("");
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [savingWorkshop, setSavingWorkshop] = useState(false);
  const [workshopMessage, setWorkshopMessage] = useState("");

  useEffect(() => {
    if (!user || !isAmbassador) {
      navigate("/login", { replace: true });
    }
  }, [user, isAmbassador, navigate]);

  useEffect(() => {
    if (!user) return;
    setRecord(user);
    setBio(user[fieldIds.bio] || "");
    setWorkshopTitle(user[fieldIds.workshopTitle] || "");
    setWorkshopDescription(user[fieldIds.workshopDescription] || "");
    setWebsiteUrl(user[fieldIds.websiteUrl] || "");
    setInstagramUrl(user[fieldIds.instagramUrl] || "");
    setTiktokUrl(user[fieldIds.tiktokUrl] || "");
  }, [user]);

  const loadEvents = async (ambassadorId: string) => {
    const all: any[] = await airtable.artistAmbassadorEvents.list({});
    const mine = all.filter((event) =>
      (event[eventFieldIds.artist] || []).includes(ambassadorId)
    );

    const taken = new Set<string>();
    if (ONE_ARTIST_PER_DATE) {
      all.forEach((event) => {
        const artists: string[] = event[eventFieldIds.artist] || [];
        if (artists.includes(ambassadorId)) return;
        const key = easternDateKey(event[eventFieldIds.dateTime]);
        if (key) taken.add(key);
      });
    }

    return { mine, taken };
  };

  const refreshEvents = async (ambassadorId: string) => {
    const { mine, taken } = await loadEvents(ambassadorId);
    setEvents(mine);
    setTakenDateKeys(taken);
  };

  useEffect(() => {
    if (!user?.id || !isAmbassador) return;
    setEventsLoading(true);
    loadEvents(user.id)
      .then(({ mine, taken }) => {
        setEvents(mine);
        setTakenDateKeys(taken);
        setEventsError(false);
      })
      .catch(() => setEventsError(true))
      .finally(() => setEventsLoading(false));
  }, [user, isAmbassador]);

  if (!user || !isAmbassador) return null;

  const monthsAvailable: string[] = MONTH_ORDER.filter((month) =>
    (record?.[fieldIds.monthsAvailable] || []).includes(month)
  );

  const eventsByDateKey: Record<string, any[]> = {};
  events.forEach((event) => {
    const key = easternDateKey(event[eventFieldIds.dateTime]);
    if (!key) return;
    if (!eventsByDateKey[key]) eventsByDateKey[key] = [];
    eventsByDateKey[key].push(event);
  });

  const registrationsFor = (dateEvents: any[]) =>
    dateEvents.reduce(
      (total, event) => total + (event[eventFieldIds.numRegistrations] ?? 0),
      0
    );

  const addDate = async (month: string, day: number) => {
    const key = dateKeyFor(month, day);
    setBusyDateKey(key);
    setAvailabilityMessage("");
    try {
      // Re-check against other artists before creating — someone may have
      // claimed this date while this page was open.
      if (ONE_ARTIST_PER_DATE) {
        const { taken } = await loadEvents(record.id);
        setTakenDateKeys(taken);
        if (taken.has(key)) {
          setAvailabilityMessage(
            `${month} ${day} was just claimed by another artist. Please pick a different date.`
          );
          setBusyDateKey("");
          return false;
        }
      }

      const year = yearForMonth(month);
      const created = await Promise.all(
        SESSION_TIMES.map((session) =>
          airtable.artistAmbassadorEvents.create({
            [eventFieldIds.artist]: [record.id],
            [eventFieldIds.dateTime]: easternTimeToISO(
              year, MONTH_INDEX[month], day, session.hour, session.minute
            ),
          })
        )
      );
      setEvents((prev) => [...prev, ...created]);
    } catch {
      setAvailabilityMessage("Something went wrong adding that date. Please try again.");
      refreshEvents(record.id).catch(() => {});
      setBusyDateKey("");
      return false;
    }
    setBusyDateKey("");
    return true;
  };

  const removeDate = async (key: string, dateEvents: any[]) => {
    setBusyDateKey(key);
    setAvailabilityMessage("");
    try {
      // Re-check registrations right before deleting — a teacher may have
      // signed up while this page was open.
      const latest = await Promise.all(
        dateEvents.map((event) => airtable.artistAmbassadorEvents.select(event.id))
      );
      if (registrationsFor(latest.filter(Boolean) as any[]) > 0) {
        setEvents((prev) =>
          prev.map((event) => {
            const updated = latest.find((item: any) => item?.id === event.id);
            return updated ? { ...event, ...updated } : event;
          })
        );
        setAvailabilityMessage(
          "An educator just signed up for this date, so it can no longer be removed."
        );
        setBusyDateKey("");
        return false;
      }

      await Promise.all(
        dateEvents.map((event) => airtable.artistAmbassadorEvents.delete(event.id))
      );
      const removedIds = dateEvents.map((event) => event.id);
      setEvents((prev) => prev.filter((event) => !removedIds.includes(event.id)));
    } catch {
      setAvailabilityMessage("Something went wrong removing that date. Please try again.");
      refreshEvents(record.id).catch(() => {});
      setBusyDateKey("");
      return false;
    }
    setBusyDateKey("");
    return true;
  };

  const confirmRemoveDate = (month: string, day: number, key: string, dateEvents: any[]) => {
    confirmAlert({
      title: "Remove date",
      message: `Remove your ${month} ${day} workshops? Both time slots will be deleted.`,
      buttons: [
        { label: "Remove", onClick: () => removeDate(key, dateEvents) },
        { label: "Cancel", onClick: () => {} },
      ],
    });
  };

  /** The date already chosen in a month, if any (only one in "one" mode). */
  const selectedDateForMonth = (month: string) => {
    for (const day of DATE_OPTIONS[month] || []) {
      const key = dateKeyFor(month, day);
      const dateEvents = eventsByDateKey[key] || [];
      if (dateEvents.length > 0) return { day, key, events: dateEvents };
    }
    return null;
  };

  const replaceDate = async (
    month: string,
    day: number,
    current: { day: number; key: string; events: any[] }
  ) => {
    const removed = await removeDate(current.key, current.events);
    if (removed) await addDate(month, day);
  };

  const confirmReplaceDate = (
    month: string,
    day: number,
    current: { day: number; key: string; events: any[] }
  ) => {
    confirmAlert({
      title: "Change date",
      message: `Switch your ${month} date to ${month} ${day}? Your ${month} ${current.day} workshops will be removed.`,
      buttons: [
        { label: "Switch", onClick: () => replaceDate(month, day, current) },
        { label: "Cancel", onClick: () => {} },
      ],
    });
  };

  const toggleDate = (month: string, day: number) => {
    const key = dateKeyFor(month, day);
    const dateEvents = eventsByDateKey[key] || [];
    if (busyDateKey) return;

    if (dateEvents.length > 0) {
      // A radio can't be unchecked — the month's date only changes by picking another.
      if (singleDatePerMonth) return;
      if (registrationsFor(dateEvents) === 0) {
        confirmRemoveDate(month, day, key, dateEvents);
      }
      return;
    }

    if (ONE_ARTIST_PER_DATE && takenDateKeys.has(key)) {
      setAvailabilityMessage(
        `${month} ${day} is already taken by another artist. Please pick a different date.`
      );
      return;
    }

    if (singleDatePerMonth) {
      const current = selectedDateForMonth(month);
      if (current) {
        if (registrationsFor(current.events) > 0) {
          setAvailabilityMessage(
            `Educators have signed up for ${month} ${current.day}, so your ${month} date can no longer be changed.`
          );
          return;
        }
        confirmReplaceDate(month, day, current);
        return;
      }
    }

    addDate(month, day);
  };

  const saveWorkshopDetails = async () => {
    setSavingWorkshop(true);
    setWorkshopMessage("");
    try {
      const fields: any = {
        [fieldIds.bio]: bio,
        [fieldIds.workshopTitle]: workshopTitle,
        [fieldIds.workshopDescription]: workshopDescription,
        [fieldIds.websiteUrl]: websiteUrl,
        [fieldIds.instagramUrl]: instagramUrl,
        [fieldIds.tiktokUrl]: tiktokUrl,
      };

      if (photoFile) {
        const uploaded = await uploadAttachment(record.id, fieldIds.photo, photoFile);
        // uploadAttachment appends — keep only the newest photo
        const attachments = uploaded?.fields?.[fieldIds.photo] || [];
        const newest = attachments[attachments.length - 1];
        if (newest?.id) fields[fieldIds.photo] = [{ id: newest.id }];
      }

      const updated = await airtable.artistAmbassadors.update(record.id, fields);
      if (updated) setRecord(updated);
      setPhotoFile(null);
      setWorkshopMessage("Workshop details saved!");
    } catch {
      setWorkshopMessage("Something went wrong saving your workshop details. Please try again.");
    }
    setSavingWorkshop(false);
  };

  const onPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file && file.size > MAX_UPLOAD_BYTES) {
      setWorkshopMessage("Photo must be 5MB or smaller.");
      return;
    }
    setWorkshopMessage("");
    setPhotoFile(file);
  };

  const currentPhoto = (record?.[fieldIds.photo] || [])[0];
  const inputClass = "block w-full rounded-md border text-black border-gray-200 py-[9px] px-3 text-sm outline-2 placeholder:text-gray-500";

  return (
    <main className="flex justify-center bg-gray-100 min-h-screen">
      <div className="relative mx-auto flex w-full max-w-[720px] flex-col space-y-4 p-4">
        <div className="flex h-16 w-full items-end justify-between rounded-lg bg-[var(--color-action-primary)] p-3 md:h-24">
          <div className="text-white">
            <AppLogo />
          </div>
          <span onClick={() => logout()} className="cursor-pointer text-sm text-white underline">
            Sign out
          </span>
        </div>

        <div className="rounded-lg bg-gray-50 px-6 pb-6 pt-4">
          <h2 className="text-2xl font-extrabold tracking-tight text-gray-900">
            Welcome, {record?.[fieldIds.name] || "Artist Ambassador"}!
          </h2>
          <p className="mt-1 text-base text-gray-600">
            Manage your event availability and workshop details below.
          </p>
        </div>

        <section className="rounded-lg bg-gray-50 px-6 pb-6 pt-4">
          <h3 className="mb-1 text-xl font-bold text-gray-900">Event Availability</h3>
          <p className="mb-4 text-sm text-gray-600">
            {singleDatePerMonth
              ? "Pick one date per month that works for you. "
              : "Check off the date that works for you. "}
            You'll be responsible for two 1-hour workshops at 9:30 AM and 2:00 PM Eastern Time.
            Each workshop has a maximum of {MAX_EVENT_CAPACITY} classroom spots.{" "}
            {ONE_ARTIST_PER_DATE
              ? "Dates already chosen by another artist are unavailable. "
              : null}
            {singleDatePerMonth
              ? "Your date can be changed as long as no educators have signed up yet."
              : "Dates can be removed as long as no educators have signed up yet."}
          </p>

          {eventsLoading ? (
            <p className="text-sm text-gray-500">Loading your dates…</p>
          ) : eventsError ? (
            <p className="text-sm text-red-600">
              We couldn't load your dates right now — please refresh to try again.
            </p>
          ) : monthsAvailable.length === 0 ? (
            <p className="text-sm text-gray-500">
              You aren't scheduled for any months yet — please contact the DWF team
              to set your available months.
            </p>
          ) : (
            <div className="space-y-5">
              {monthsAvailable.map((month) => {
                const monthSelection = selectedDateForMonth(month);
                // In "one" mode a booked date locks the whole month, since
                // switching would mean deleting a date teachers signed up for.
                const monthLocked =
                  singleDatePerMonth &&
                  !!monthSelection &&
                  registrationsFor(monthSelection.events) > 0;

                return (
                  <div key={month}>
                    <p className="mb-2 text-sm font-semibold text-gray-800">
                      {month} {yearForMonth(month)}
                    </p>
                    <div className="space-y-2">
                      {(DATE_OPTIONS[month] || []).map((day) => {
                        const key = dateKeyFor(month, day);
                        const dateEvents = eventsByDateKey[key] || [];
                        const selected = dateEvents.length > 0;
                        const takenByOther =
                          ONE_ARTIST_PER_DATE && !selected && takenDateKeys.has(key);
                        const registrations = registrationsFor(dateEvents);
                        const locked =
                          takenByOther ||
                          (selected && registrations > 0) ||
                          monthLocked;
                        const busy = busyDateKey === key;

                        return (
                          <div
                            key={key}
                            className={`rounded-lg border bg-white p-3 ${
                              selected
                                ? "border-[var(--color-action-primary)]"
                                : takenByOther
                                  ? "border-gray-200 bg-gray-50"
                                  : "border-gray-200"
                            }`}
                          >
                            <label
                              className={`flex items-center gap-3 ${
                                locked || (singleDatePerMonth && selected)
                                  ? "cursor-not-allowed"
                                  : "cursor-pointer"
                              }`}
                              title={
                                takenByOther
                                  ? "This date is already taken by another artist."
                                  : locked
                                    ? selected
                                      ? "Educators have signed up for this date, so it can no longer be removed."
                                      : `Educators have signed up for ${month} ${monthSelection?.day}, so your ${month} date can no longer be changed.`
                                    : undefined
                              }
                            >
                              <input
                                type={singleDatePerMonth ? "radio" : "checkbox"}
                                name={singleDatePerMonth ? `date-${month}` : undefined}
                                className="h-4 w-4"
                                checked={selected}
                                disabled={busy || locked || !!busyDateKey}
                                onChange={() => toggleDate(month, day)}
                              />
                              <span
                                className={`text-sm ${
                                  takenByOther ? "text-gray-400" : "text-gray-800"
                                }`}
                              >
                                {month} {day}
                              </span>
                              {busy ? (
                                <span className="ml-auto text-xs text-gray-500">Saving…</span>
                              ) : takenByOther ? (
                                <span className="ml-auto rounded-full bg-gray-200 px-3 py-1 text-xs font-medium text-gray-600">
                                  Taken by another artist
                                </span>
                              ) : locked ? (
                                <span className="ml-auto rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
                                  Educators signed up
                                </span>
                              ) : null}
                            </label>
                            {selected ? (
                              <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 pl-7 text-xs text-gray-600">
                                {dateEvents
                                  .slice()
                                  .sort((a, b) =>
                                    String(a[eventFieldIds.dateTime]).localeCompare(
                                      String(b[eventFieldIds.dateTime])
                                    )
                                  )
                                  .map((event) => (
                                    <span key={event.id}>
                                      {formatSessionTime(event[eventFieldIds.dateTime])}
                                      {": "}
                                      {event[eventFieldIds.numRegistrations] ?? 0}/
                                      {MAX_EVENT_CAPACITY} signed up
                                    </span>
                                  ))}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {availabilityMessage && (
            <p className="mt-3 text-sm text-red-600">{availabilityMessage}</p>
          )}
        </section>

        <section className="rounded-lg bg-gray-50 px-6 pb-6 pt-4">
          <h3 className="mb-3 text-xl font-bold text-gray-900">Workshop Details</h3>

          <p className="mb-2 text-xs font-medium text-gray-900">Photo</p>
          <div className="flex items-center gap-4">
            {currentPhoto?.url && (
              <img
                src={currentPhoto.url}
                alt="Current profile"
                className="h-20 w-20 rounded-full object-cover"
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={onPhotoChange}
              disabled={savingWorkshop}
              className="text-sm text-gray-600"
            />
          </div>

          <label className="mb-2 mt-4 block text-xs font-medium text-gray-900" htmlFor="bio">
            Bio
          </label>
          <textarea
            id="bio"
            className={inputClass}
            rows={4}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            disabled={savingWorkshop}
            placeholder="Tell educators about yourself"
          />

          <label className="mb-2 mt-4 block text-xs font-medium text-gray-900" htmlFor="workshopTitle">
            Workshop Title
          </label>
          <input
            id="workshopTitle"
            className={inputClass}
            value={workshopTitle}
            onChange={(e) => setWorkshopTitle(e.target.value)}
            disabled={savingWorkshop}
            placeholder="Your workshop's title"
          />

          <label className="mb-2 mt-4 block text-xs font-medium text-gray-900" htmlFor="workshopDescription">
            Workshop Description
          </label>
          <textarea
            id="workshopDescription"
            className={inputClass}
            rows={8}
            value={workshopDescription}
            onChange={(e) => setWorkshopDescription(e.target.value)}
            disabled={savingWorkshop}
            placeholder="Describe your workshop"
          />

          <label className="mb-2 mt-4 block text-xs font-medium text-gray-900" htmlFor="websiteUrl">
            Website URL
          </label>
          <input
            id="websiteUrl"
            type="url"
            className={inputClass}
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            disabled={savingWorkshop}
            placeholder="https://your-website.com"
          />

          <label className="mb-2 mt-4 block text-xs font-medium text-gray-900" htmlFor="instagramUrl">
            Instagram URL
          </label>
          <input
            id="instagramUrl"
            type="url"
            className={inputClass}
            value={instagramUrl}
            onChange={(e) => setInstagramUrl(e.target.value)}
            disabled={savingWorkshop}
            placeholder="https://instagram.com/your-handle"
          />

          <label className="mb-2 mt-4 block text-xs font-medium text-gray-900" htmlFor="tiktokUrl">
            TikTok URL
          </label>
          <input
            id="tiktokUrl"
            type="url"
            className={inputClass}
            value={tiktokUrl}
            onChange={(e) => setTiktokUrl(e.target.value)}
            disabled={savingWorkshop}
            placeholder="https://tiktok.com/@your-handle"
          />

          <div className="mt-4 flex items-center gap-4">
            <Button
              buttonType="primary"
              onClick={saveWorkshopDetails}
              disabled={savingWorkshop}
              aria-disabled={savingWorkshop}
            >
              {savingWorkshop ? "Saving..." : "Save Workshop Details"}
            </Button>
            {workshopMessage && (
              <p className="text-sm text-gray-600">{workshopMessage}</p>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}
